// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListFilterAccordionSectionStyles } from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";

import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const { searchSettings } = getSettingsCollection(state);

    // Xng-Change
    // store getProductListDataView(state) into productData
    const productData = getProductListDataView(state);
    return {
        enabled:
            searchSettings.enableBoostingByPurchaseHistory &&
            searchSettings.allowFilteringForPreviouslyPurchasedProducts &&
            (session.isAuthenticated || session.rememberMe) &&
            !session.isGuest,
        loaded: !!productData.value,
        previouslyPurchasedProducts: state.pages.productList.productFilters.previouslyPurchasedProducts,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const productListPreviouslyPurchasedFilterStyles: ProductListFilterAccordionSectionStyles = {};

const ProductListPreviouslyPurchasedFilter = ({
    enabled,
    loaded,
    previouslyPurchasedProducts,
    addProductFilters,
    removeProductFilters,
}: Props) => {
    if (!enabled || !loaded) {
        return null;
    }

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ previouslyPurchasedProducts: false });
        } else {
            addProductFilters({ previouslyPurchasedProducts: true });
        }
    };

    const facet = [
        {
            id: "previouslyPurchased",
            // XNG-Change:change translation
            name: translate("Previously Purchased"),
            count: -1,
            selected: previouslyPurchasedProducts ?? false,
        },
    ];

    // XNG-Changes: change structure
    return (
        <>
            {facet?.map(facet => (
                <CheckboxGroup key={facet.id} {...productListPreviouslyPurchasedFilterStyles.checkBoxGroup}>
                    <Checkbox
                        {...(facet.selected
                            ? productListPreviouslyPurchasedFilterStyles.checkBoxSelected
                            : productListPreviouslyPurchasedFilterStyles.checkBox)}
                        checked={facet.selected}
                        onChange={() => onChangeFacet(facet)}
                        data-test-selector={`facetFilter${facet.id}`}
                    >
                        {facet.name}
                    </Checkbox>
                </CheckboxGroup>
            ))}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPreviouslyPurchasedFilter),
    definition: {
        group: "Product List",
        displayName: "Previously Purchased Filter",
        // XNG-Changes: add CategoryDetailsPage
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
